/**
 * Plug-and-play ecom setup: rewrite Amazon / Walmart hrefs and wire clicks.
 */

import { AwaitFor } from '../../core/events/awaiter';
import type { SdkContext } from '../types';
import { buildAmazonUrl, type RuntimeAdTagAssignment } from './amazon';
import { getRuntimeAdTagAssignment } from './adTagAssignment';
import { resolveSessionTag } from './sessionTagAssignment';
import { buildAmazonOpts } from './amazonOpts';
import type { AdAccountConfig } from '../../core/config/types';
import {
  wireEcomLinks,
  setEcomProductMarket,
  DEFAULT_ECOM_LINK_SELECTOR,
  type WireEcomLinksOptions,
} from './links';
import { resolveEcomProductMarket } from './market';
import {
  buildWalmartImpactUrl,
  isWalmartImpactUrl,
  isWalmartUrl,
} from './walmart';
import { warn } from '../../util/log';

/** Last `ecom.init` teardown per container (avoids duplicate click handlers on re-init). */
const activeInitByRoot = new WeakMap<Element | Document, () => void>();

export interface EcomInitOptions extends WireEcomLinksOptions {
  /** Element, document, or CSS selector for affiliate links. Default `document`. */
  container?: Document | Element | string;
}

export function resolveContainer(container?: Document | Element | string): Element | Document {
  if (container === undefined || container === null) {
    if (typeof document !== 'undefined') return document;
    throw new Error('[kueez-sdk/ecom] document is not available');
  }
  if (typeof container === 'string') {
    if (typeof document === 'undefined') {
      throw new Error('[kueez-sdk/ecom] document is not available');
    }
    const el = document.querySelector(container);
    if (!el) {
      throw new Error(`[kueez-sdk/ecom] container not found: ${container}`);
    }
    return el;
  }
  return container;
}

/** Explicit `defaultTag` wins; else website setting `ecom_amazon_default_tag`. */
export function resolveDefaultTag(
  ctx: SdkContext,
  explicit?: string,
): string | null {
  const fromOpts = explicit?.trim();
  if (fromOpts) return fromOpts;
  const fromConfig = ctx.getConfig().ecom.amazonDefaultTag?.trim();
  return fromConfig || null;
}

export function tagEcomLinkHrefs(
  ctx: SdkContext,
  adAccount: AdAccountConfig | null,
  container: Element | Document,
  opts: Pick<WireEcomLinksOptions, 'defaultTag' | 'linkSelector' | 'enforceTagsPrefix'> & {
    runtimeAdTagAssignment?: RuntimeAdTagAssignment | null;
    sessionTag?: string | null;
  },
): void {
  const defaultTag = resolveDefaultTag(ctx, opts.defaultTag);
  if (!defaultTag) {
    warn('ecom tag rewrite skipped — no defaultTag and no ecom_amazon_default_tag');
    return;
  }

  const enforceTagsPrefix = opts.enforceTagsPrefix !== false;
  const selector = opts.linkSelector ?? DEFAULT_ECOM_LINK_SELECTOR;
  const amazonOpts = buildAmazonOpts(
    ctx,
    adAccount,
    defaultTag,
    opts.runtimeAdTagAssignment,
    opts.sessionTag,
    enforceTagsPrefix,
  );
  const config = ctx.getConfig();
  const sessionId = ctx.getIdentity().sessionId;
  const mbid = ctx.getPageParams().mediaBuyingId;
  const anchors =
    container === document
      ? document.querySelectorAll<HTMLAnchorElement>(selector)
      : container.querySelectorAll<HTMLAnchorElement>(selector);

  for (const anchor of anchors) {
    const href = anchor.getAttribute('href');
    if (!href || href === '#') continue;

    // Walmart → Impact (skip Amazon assign paths)
    if (isWalmartUrl(href) || isWalmartImpactUrl(href)) {
      try {
        const destination = isWalmartImpactUrl(href)
          ? (new URL(href).searchParams.get('u') || href)
          : href;
        const built = buildWalmartImpactUrl({
          destinationUrl: destination,
          sessionId,
          mbid,
          websiteDomain: config.website.domain,
        });
        anchor.setAttribute('href', built.url);
        anchor.setAttribute('data-kz-ecsid', built.clickId);
        anchor.setAttribute('data-kz-epm', 'walmart');
        anchor.setAttribute('data-kz-dest', built.destinationUrl);
        anchor.removeAttribute('data-kz-ecptg');
        anchor.removeAttribute('data-kz-eatag');
        anchor.removeAttribute('data-kz-eataf');
        anchor.removeAttribute('data-kz-eataid');
        anchor.removeAttribute('data-kz-eatafr');
        anchor.removeAttribute('data-kz-eatas');
      } catch {
        /* invalid URL — leave href unchanged */
      }
      continue;
    }

    if (href.indexOf('amazon.') === -1) continue;
    try {
      const built = buildAmazonUrl(href, amazonOpts);
      anchor.setAttribute('href', built.url);
      anchor.setAttribute('data-kz-ecsid', built.clickId);
      anchor.removeAttribute('data-kz-epm');
      anchor.removeAttribute('data-kz-dest');
      if (opts.runtimeAdTagAssignment) {
        anchor.setAttribute('data-kz-eatag', '1');
        anchor.setAttribute('data-kz-eataf', opts.runtimeAdTagAssignment.fallback ? '1' : '0');
        if (opts.runtimeAdTagAssignment.assignmentId) {
          anchor.setAttribute('data-kz-eataid', opts.runtimeAdTagAssignment.assignmentId);
        } else {
          anchor.removeAttribute('data-kz-eataid');
        }
        if (opts.runtimeAdTagAssignment.fallbackReason) {
          anchor.setAttribute('data-kz-eatafr', opts.runtimeAdTagAssignment.fallbackReason);
        } else {
          anchor.removeAttribute('data-kz-eatafr');
        }
        if (opts.runtimeAdTagAssignment.fallbackStatus) {
          anchor.setAttribute('data-kz-eatas', String(opts.runtimeAdTagAssignment.fallbackStatus));
        } else {
          anchor.removeAttribute('data-kz-eatas');
        }
      } else {
        anchor.removeAttribute('data-kz-eatag');
        anchor.removeAttribute('data-kz-eataf');
        anchor.removeAttribute('data-kz-eataid');
        anchor.removeAttribute('data-kz-eatafr');
        anchor.removeAttribute('data-kz-eatas');
      }
      if (built.ecptg) {
        anchor.setAttribute('data-kz-ecptg', built.ecptg);
      } else {
        anchor.removeAttribute('data-kz-ecptg');
      }
    } catch {
      /* invalid URL — leave href unchanged */
    }
  }
}

/** Resolves with the served post, or `undefined` when no pool content ran. */
async function waitForReadyAndPoolIdle(ctx: SdkContext): Promise<unknown> {
  await ctx.awaitFor(AwaitFor.READY);

  return new Promise<unknown>((resolve) => {
    let settled = false;
    const finish = (post?: unknown) => {
      if (settled) return;
      settled = true;
      resolve(post);
    };

    void ctx.awaitFor(AwaitFor.CONTENT).then(finish);

    setTimeout(() => {
      if (!ctx.isAwaitCompleted(AwaitFor.CONTENT) && ctx.contentFetchInFlight() === 0) {
        finish();
      }
    }, 0);
  });
}

export function runEcomInit(
  ctx: SdkContext,
  adAccount: AdAccountConfig | null,
  opts: EcomInitOptions = {},
): () => void {
  let unwire: (() => void) | null = null;
  let tornDown = false;

  const teardown = () => {
    if (tornDown) return;
    tornDown = true;
    unwire?.();
    unwire = null;
    if (containerRef) {
      const current = activeInitByRoot.get(containerRef);
      if (current === teardown) activeInitByRoot.delete(containerRef);
    }
  };

  let containerRef: Element | Document | null = null;

  void (async () => {
    try {
      const post = await waitForReadyAndPoolIdle(ctx);
      if (tornDown) return;
      setEcomProductMarket(resolveEcomProductMarket(ctx, post));

      const container = resolveContainer(opts.container);
      const prevTeardown = activeInitByRoot.get(container);
      if (prevTeardown) prevTeardown();

      const wireOpts: WireEcomLinksOptions = {
        defaultTag: resolveDefaultTag(ctx, opts.defaultTag) ?? undefined,
        linkSelector: opts.linkSelector,
        ...(opts.enforceTagsPrefix === false ? { enforceTagsPrefix: false } : {}),
      };
      if (!wireOpts.defaultTag) {
        warn('ecom.init — no defaultTag and no ecom_amazon_default_tag; Amazon links may be untagged');
      }
      const runtimeAdTagAssignment = await getRuntimeAdTagAssignment(ctx, adAccount);
      // Runtime /assign/ad wins; otherwise session /assign when estcb is set.
      const sessionTag =
        runtimeAdTagAssignment && !runtimeAdTagAssignment.fallback
          ? null
          : !runtimeAdTagAssignment
            ? await resolveSessionTag(ctx, adAccount)
            : null;

      tagEcomLinkHrefs(ctx, adAccount, container, {
        ...wireOpts,
        runtimeAdTagAssignment,
        sessionTag,
      });
      if (tornDown) return;

      unwire = wireEcomLinks(ctx, container, wireOpts);

      activeInitByRoot.set(container, teardown);
      containerRef = container;
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.warn('[kueez-sdk/ecom] init failed', err);
      }
    }
  })();

  return teardown;
}
